import {
    initializeFaro,
    PerformanceInstrumentation,
    WebVitalsInstrumentation
} from '@grafana/faro-web-sdk';
import { map } from '@libs/shared';
import { ROUTER, SITE_ROUTER } from '~/constants';

(() => {
    const faroConfig = window.jdy_config.faro;
    if (!faroConfig?.url) {
        return;
    }
    const shouldSample = Math.random() < faroConfig.sample_ratio;

    if (shouldSample) {
        // PerformanceInstrumentation 先依赖 navigation entry 启动，再开始 resource 采集。
        // 如果把当前文档导航 URL 忽略掉，会导致后续没有任何 resource 数据。
        const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const currentDocumentUrlPattern = `${escapeRegExp(
            `${window.location.origin}${window.location.pathname}${window.location.search}`
        )}(?:#.*)?`;
        const resourcePattern =
            'https?:\\/\\/(?:[^/]+\\.)?(?:jiandaoyun\\.com|jdydevelop\\.com|jdycdn\\.com|finecloud\\.com|frjdy\\.com)\\/.*\\.(?:js|css)(?:\\?.*)?';

        const ignoreNonAllowedUrls = new RegExp(
            `^(?!(?:${currentDocumentUrlPattern}|${resourcePattern})$).*`,
            'i'
        );

        const normalizePath = (path = '/') => {
            if (path.endsWith('/')) {
                return path.slice(0, -1);
            }
            return path;
        };

        const getPagePathInfoFromRouter = (router: string) => {
            const [pathWithSearch, hash = ''] = router.split('#');
            return {
                path: normalizePath(pathWithSearch?.split('?')[0]),
                hash: normalizePath(hash)
            };
        };

        const collectRouterPagePaths = (
            router: unknown,
            pagePaths: string[],
            hashPaths?: string[]
        ) => {
            if (typeof router === 'string') {
                const { path, hash } = getPagePathInfoFromRouter(router);
                pagePaths.push(path);
                if (hash && hashPaths) {
                    hashPaths.push(hash);
                }
                return;
            }

            if (!router || typeof router !== 'object') {
                return;
            }

            Object.keys(router).forEach((key) => {
                collectRouterPagePaths(
                    (router as Record<string, unknown>)[key],
                    pagePaths,
                    hashPaths
                );
            });
        };

        const getStaticSegmentCount = (path: string) => {
            return path.split('/').filter((segment) => segment && !segment.startsWith(':')).length;
        };

        const getRouterPagePatterns = (paths: string[]) => {
            return map(paths, (path) => {
                return {
                    template: path,
                    pattern: new RegExp(
                        `^${escapeRegExp(path)
                            .replace(/\/:[^/]+\\\?/g, '(?:/[^/]+)?')
                            .replace(/:[^/]+/g, '[^/]+')}$`
                    ),
                    staticSegmentCount: getStaticSegmentCount(path)
                };
            });
        };

        const getMatchedPageTemplate = (
            pagePath: string,
            pagePatterns: ReturnType<typeof getRouterPagePatterns>
        ) => {
            if (!pagePath) {
                return null;
            }
            return pagePatterns
                .filter(({ pattern }) => pattern.test(pagePath))
                .sort((left, right) => right.staticSegmentCount - left.staticSegmentCount)[0]
                ?.template;
        };

        const normalizeUnknownRoutePath = (path: string) => {
            if (!path) {
                return path;
            }
            return path.replace(/(^|\/)[0-9a-f]{24}(?=\/|$)/gi, '$1:var');
        };

        const sitePagePaths: string[] = [];
        const hashPagePaths: string[] = [];
        collectRouterPagePaths(SITE_ROUTER, sitePagePaths, hashPagePaths);
        collectRouterPagePaths(ROUTER, hashPagePaths);

        const sitePagePatterns = getRouterPagePatterns(sitePagePaths);
        const hashPagePatterns = getRouterPagePatterns(hashPagePaths);

        initializeFaro({
            url: faroConfig.url,
            apiKey: faroConfig.key,
            app: {
                name: 'fx-corp-web-pc',
                environment: faroConfig.environment
            },
            instrumentations: [
                new WebVitalsInstrumentation(),
                // Navigation & Resource Performance
                new PerformanceInstrumentation()
            ],
            // 默认只采集 fetch/XHR，设置为 true，则会采集全部 resource 资源
            trackResources: true,
            ignoreUrls: [ignoreNonAllowedUrls],
            pageTracking: {
                generatePageId(location) {
                    const sitePath = normalizePath(location.pathname);
                    const siteHash = normalizePath(location.hash.slice(1));

                    const matchedPathTemplate = getMatchedPageTemplate(sitePath, sitePagePatterns);
                    if (!siteHash) {
                        return matchedPathTemplate || normalizeUnknownRoutePath(sitePath);
                    }

                    const matchedHashTemplate = getMatchedPageTemplate(siteHash, hashPagePatterns);
                    return `${matchedPathTemplate || normalizeUnknownRoutePath(sitePath)}#${
                        matchedHashTemplate || siteHash
                    }`;
                }
            }
        });
    }
})();
